import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { generationsService } from '../../services/generationsService';

const POLL_INTERVAL = 5000; // 5 seconds

const GiftBoxModal = ({
  isOpen,
  onClose,
  generation,
  setToastMessage,
  setToastActive,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [giftBoxImageUrl, setGiftBoxImageUrl] = useState(null);
  const [giftBoxPdfUrl, setGiftBoxPdfUrl] = useState(null);
  const [giftBoxStatus, setGiftBoxStatus] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  // Update local state when generation changes
  useEffect(() => {
    if (generation) {
      setGiftBoxImageUrl(generation.gift_box_image_url || null);
      setGiftBoxPdfUrl(generation.gift_box_pdf_url || null);
      setGiftBoxStatus(generation.gift_box_status || null);
      setError('');
    }
  }, [generation]);

  // Polling logic
  useEffect(() => {
    const shouldPoll = isOpen && generation?.generation_id &&
      (giftBoxStatus === 'queued' || giftBoxStatus === 'generating' || (giftBoxStatus === 'completed' && !giftBoxPdfUrl));

    if (shouldPoll) {
      pollRef.current = setInterval(async () => {
        try {
          const result = await generationsService.getGiftBox(generation.generation_id);
          const data = result?.data;
          if (data?.gift_box_status === 'completed' && data?.gift_box_image_url) {
            setGiftBoxImageUrl(data.gift_box_image_url);
            if (data.gift_box_pdf_url) {
              setGiftBoxPdfUrl(data.gift_box_pdf_url);
            }
            setGiftBoxStatus('completed');
            setError('');
            // Only show toast once when image first arrives (not on PDF poll)
            if (!giftBoxImageUrl) {
              if (setToastMessage) setToastMessage('Gift box image generated successfully!');
              if (setToastActive) setToastActive(true);
            }
          } else if (data?.gift_box_status === 'failed') {
            setGiftBoxStatus('failed');
            setError(data.gift_box_error || 'Generation failed');
          } else {
            setGiftBoxStatus(data?.gift_box_status || giftBoxStatus);
          }
        } catch (err) {
          // Silently retry on poll errors
          console.error('Gift box poll error:', err);
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isOpen, generation?.generation_id, giftBoxStatus]);

  const handleGenerate = async () => {
    const genId = generation?.generation_id;
    if (!genId) return;

    setIsGenerating(true);
    setError('');
    try {
      const result = await generationsService.generateGiftBox(genId);
      if (result?.success) {
        // Job was queued - start polling
        setGiftBoxStatus('queued');
        if (setToastMessage) setToastMessage('Gift box generation queued!');
        if (setToastActive) setToastActive(true);
      } else {
        setError(result?.error || 'Failed to queue gift box generation');
      }
    } catch (err) {
      setError(err?.message || 'Failed to queue gift box generation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPdf = () => {
    if (giftBoxPdfUrl) {
      window.open(giftBoxPdfUrl, '_blank');
    }
  };

  const handleDownloadImage = () => {
    if (giftBoxImageUrl) {
      window.open(giftBoxImageUrl, '_blank');
    }
  };

  const isProcessing = giftBoxStatus === 'queued' || giftBoxStatus === 'generating';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{ padding: '16px' }}>{'🎁 Gift Box Image'}</div>}
      secondaryActions={[
        {
          content: 'Close',
          onAction: onClose,
        },
      ]}
      footer={<div style={{ padding: '16px' }}></div>}
    >
      <div style={{ padding: '16px' }}>
        <BlockStack gap="400">
          {/* Generation Info */}
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              Generation: {generation?.generation_id || 'N/A'}
            </Text>
            <Text variant="bodySm" as="p" color="subdued">
              Kid: {generation?.kid_name || 'N/A'} | Order: {generation?.order_id || 'N/A'}
            </Text>
          </BlockStack>

          {/* Error Banner */}
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              backgroundColor: '#fff8e6',
              borderRadius: '12px',
              border: '2px solid #f0c040'
            }}>
              <BlockStack gap="200" align="center">
                <Spinner size="large" />
                <Text variant="headingSm" as="p" alignment="center">
                  ⏳ Generating gift box image...
                </Text>
                <Text variant="bodySm" as="p" alignment="center" color="subdued">
                  Job is in queue. This page will update automatically when ready.
                </Text>
                <Text variant="bodySm" as="p" alignment="center" tone="subdued">
                  Status: <strong>{giftBoxStatus}</strong>
                </Text>
              </BlockStack>
            </div>
          )}

          {/* Gift Box Image (completed) */}
          {giftBoxImageUrl && !isProcessing ? (
            <BlockStack gap="300">
              <div style={{
                border: '2px solid #e3e3e3',
                borderRadius: '12px',
                overflow: 'hidden',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={giftBoxImageUrl}
                  alt="Gift Box"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '500px',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <InlineStack gap="300" align="center">
                {giftBoxPdfUrl ? (
                  <Button onClick={handleDownloadPdf} variant="primary">
                    📥 Download PDF
                  </Button>
                ) : (
                  <Button disabled variant="primary">
                    ⏳ PDF Generating...
                  </Button>
                )}
                <Button onClick={handleDownloadImage} variant="secondary">
                  🖼️ Download Image
                </Button>
                <Button
                  onClick={handleGenerate}
                  loading={isGenerating}
                  variant="secondary"
                >
                  🔄 Regenerate
                </Button>
              </InlineStack>
            </BlockStack>
          ) : !isProcessing && (
            /* No image and not processing - show generate button */
            <BlockStack gap="300" align="center">
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '12px',
                border: '2px dashed #d4d4d4'
              }}>
                <BlockStack gap="200" align="center">
                  <Text variant="headingMd" as="p" alignment="center">
                    🎁
                  </Text>
                  <Text variant="bodyMd" as="p" alignment="center">
                    No gift box image generated yet
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Button onClick={handleGenerate} variant="primary" loading={isGenerating}>
                      ✨ Generate Gift Box Image
                    </Button>
                  </div>
                </BlockStack>
              </div>
            </BlockStack>
          )}
        </BlockStack>
      </div>
    </Modal>
  );
};

export default GiftBoxModal;
